import React, { useState } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { getDoc, doc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useLanguage, LanguageToggle } from "../i18n";
import "./SignIn.css"; // Import the CSS file

const SignIn = ({ setUserRole }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  // Forgot password view
  const [mode, setMode] = useState("signin"); // "signin" | "reset"
  const [resetEmail, setResetEmail] = useState("");
  const [resetSending, setResetSending] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState("");

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");

    try {
      // Firebase Auth login
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;

      // Fetch additional user details from Firestore
      const fetchUserData = async (uid) => {
        try {
          const userDoc = await getDoc(doc(db, "users", uid)); // Fetch document with uid as ID
          if (userDoc.exists()) {
            return userDoc.data(); // Return user data
          } else {
            throw new Error("No such user data in Firestore!");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          throw error;
        }
      };

      const userData = await fetchUserData(userId); // Fetch user data
      setUserRole(userData.role); // Set user role in the parent component
      navigate("/"); // Redirect to Home
    } catch (err) {
      setError(t("Invalid email or password. Please try again."));
      console.error("Sign-in error:", err);
    }
  };

  const openReset = () => {
    setResetEmail(email);
    setResetSent(false);
    setResetError("");
    setMode("reset");
  };

  const backToSignIn = () => {
    setResetError("");
    setMode("signin");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetSent(false);
    const addr = resetEmail.trim();
    if (!addr) {
      setResetError(t("Enter your email address."));
      return;
    }
    setResetSending(true);
    try {
      await sendPasswordResetEmail(auth, addr);
      setResetSent(true);
    } catch (err) {
      const code = err?.code || "";
      // Never reveal whether an account exists for this email.
      if (code === "auth/user-not-found") {
        setResetSent(true);
      } else if (code === "auth/invalid-email" || code === "auth/missing-email") {
        setResetError(t("That email address doesn't look right. Check it and try again."));
      } else if (code === "auth/network-request-failed") {
        setResetError(t("No connection. Check your signal and try again."));
      } else if (code === "auth/too-many-requests") {
        setResetError(t("Too many tries. Wait a few minutes and try again."));
      } else {
        setResetError(t("The reset link could not be sent. Try again."));
        console.error("Password reset error:", err);
      }
    } finally {
      setResetSending(false);
    }
  };

  return (
    <div className="sign-in-container">
      <div className="sign-in-box">
        <div className="sign-in-lang">
          <LanguageToggle />
        </div>
        <img src="/logo.png" alt="Servicing HQ" className="logo" />

        {mode === "signin" ? (
          <>
            <h1>{t("Sign In")}</h1>
            <form onSubmit={handleSignIn}>
              <input
                type="email"
                placeholder={t("Email")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder={t("Password")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button type="submit">{t("Sign In")}</button>
            </form>
            {error && <p className="error-message">{error}</p>}
            <button type="button" className="sign-in-link" onClick={openReset}>
              {t("Forgot password?")}
            </button>
          </>
        ) : (
          <>
            <h1>{t("Reset Password")}</h1>
            <p className="sign-in-help">
              {t("Enter the email you sign in with. We'll email you a link to set a new password.")}
            </p>
            <form onSubmit={handleReset}>
              <input
                type="email"
                placeholder={t("Email")}
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
              />
              <button type="submit" disabled={resetSending}>
                {resetSending ? t("Sending…") : t("Send reset link")}
              </button>
            </form>
            {resetSent && (
              <p className="sign-in-success">
                {t("If an account exists for that email, a reset link has been sent. Check your inbox and spam folder.")}
              </p>
            )}
            {resetError && <p className="error-message">{resetError}</p>}
            <button type="button" className="sign-in-link" onClick={backToSignIn}>
              {t("Back to sign in")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SignIn;
